import React, {useState} from "react";
import { Button, Checkbox, Control, Field, Select } from "@allxsmith/bestax-bulma";
import axios from "axios";
import {
  updateCustomerInvoice,
  updateHireCustomerInvoice,
  updateWorkshopInvoice
} from "./updateInvoice";
import {readableDate, formatCurrency, expenseDueDate, formatDate} from "../../utils";
import useSafeSave from "../../hooks/useSafeSave";
import useEditableTableTextCell from "../../hooks/useEditableTableTextCell";
import useEditableTableDateCell from "../../hooks/useEditableTableDateCell";
import useEditableTableTextAreaCell from "../../hooks/useEditableTableTextAreaCell";
import WorkTypePicker from "../Job/WorkTypePicker";
import PaymentTypePicker from "../Settings/PaymentTypes/PaymentTypePicker";
import NominalCodePicker from "../Settings/NominalCodes/NominalCodePicker";
import TrackingOptionsPicker from "../Settings/TrackingCategory/TrackingOptionsPicker";
import TrackingCategory from "../Settings/TrackingCategory/TrackingCategory";
import useEditableTableCostCell from "../../hooks/useEditableTableCostCell";
import VatSelector from "../Utils/VatSelector";
import TaxTypePicker from "../Settings/NominalCodes/TaxTypePicker";
import {ToastContainer, toast} from "react-toastify";

export const PORows = ({
                         value,
                         onApprove,
                         printCheck,
                         selected,
                         endpoint,
                         refresh,
                         setInvoiceNo,
                         setCreditNo,
                         updateValue,
                         approvedView,
                         workTypes,
                         onWorkTypeChange,
                         paymentTypes,
                         onPaymentTypeChange,
                         nominalCodes,
                         codeObjects,
                         trackingOptions,
                         trackingOptionObjects
                       }) => {
  let rows = [];
  const [editable, setEditable] = useState(false);
  const [workEditable, setWorkEditable] = useState(false); // editable non job fields
  const [partEditable, setPartEditable] = useState(false);
  const [lastEdited, setLastEdited] = useState();

  const onCheck = e => {
    e.stopPropagation();
    printCheck(value);
  };

  const onInvoiceChange = e => {
    makeDirty();
    setLastEdited(new Date());
    setInvoiceNo(e.target.value);
  };

  const onCreditChange = e => {
    makeDirty();
    setLastEdited(new Date());
    setCreditNo(e.target.value);
  };


  const onChange = e => {
    makeDirty();
    setLastEdited(new Date());
    let newWork = {...value};
    newWork[e.target.name] = e.target.value;
    updateValue(newWork);
  };

  const onApprovalReceived = (e) => {
      e.stopPropagation();
      let toUpdate = {
          target: {
              name: e.target.name,
              value: !value.approval_received
          }
      }
      onChange(toUpdate);
  }

  const onNominalChange = e => {
      e.stopPropagation();
      const toUpdate = {
          target: {
              name: e.target.name,
              value: codeObjects[e.target.value]
          }
      };
      onChange(toUpdate);
  }

  const onTrackingOptionChange = e => {
      e.stopPropagation();
      const toUpdate = {
          target: {
              name: e.target.name,
              value: trackingOptionObjects[e.target.value]
          }
      }
      onChange(toUpdate);
  }

  const onWorkSave = () => {
    let dataToSave = value;
    const conf = {
        data: dataToSave,
        method: "put",
        url: endpoint + "work/update/"+ value.id
    }
    axios(conf).then(res => {
        setWorkEditable(false);
        refresh();
    }).catch(err => {
        toast.error("There was an error", {position:  "top-right"})
    })
  }

  const onSave = e => {
    return updateWorkshopInvoice(
      endpoint,
      value.job,
      value.job_workshop_invoice_number,
      value.job_workshop_payment_date || null,
      value.workshop_invoice_date || null,
      value.job_workshop_credit_number
    ).then(() => {
      refresh();
      setEditable(!editable);
    });
  };

  const onSavePart = (partIndex) => {
      const conf = {
          method: "put",
          url: endpoint+"parts/update/"+value.parts[partIndex].id,
          data: value.parts[partIndex]
      }
      axios(conf).then(res => {
          refresh();
          setPartEditable(!partEditable);
      }).catch(err => {
          toast.error("There was an error",  {position: "top-right"});
      })
  };

  const onPartNominalChange = (e, workItem, partIndex) =>{
      const toUpdate = {
          target: {
              name: e.target.name,
              value: codeObjects[e.target.value]
          }
      };
      onPartChange(toUpdate, workItem, partIndex)
  }

  const onPartTrackingOptionChange = (e, workItem, partIndex) =>{
      const toUpdate = {
          target: {
              name: e.target.name,
              value: trackingOptionObjects[e.target.value]
          }
      };
      onPartChange(toUpdate, workItem, partIndex)
  }

  const onPartChange = (e, workItem, partIndex) => {
      let newParts = [...workItem.parts];
      newParts[partIndex][e.target.name] = e.target.value;
      const toUpdate = {
          target: {
              name: "parts",
              value: newParts
          }
      };
      onChange(toUpdate);
  }


  const checkUpdated = () => {
    return axios
      .get(endpoint + "work/" + value.id)
      .then(
        work =>
          (new Date(work.data.last_saved).getTime() -
            new Date(value.last_saved).getTime()) /
          1000 <
          1
      );
  };

  const [makeDirty, safeSave] = useSafeSave(
    onSave,
    lastEdited,
    undefined,
    checkUpdated
  );

  const handleDueDate = () => {
    let date = expenseDueDate(value, "workshop_invoice_date", "workshop_details");
    if(date){
      return readableDate(formatDate(date));
    }
    else{
      return ""
    }
  }

  const firstRow = (
    <tr
      className="clickable table-highlight-orange small-row-white"
      onClick={() => window.open(`/editjob/${value.job}`, "_self")}
      key={value.id}
    >
      <td>{value.job}</td>
      <td>{value.vehicle_registration}</td>
      <td>{value.workshop} </td>
      <td>{value.chargeable ? value.customer2 : value.customer} </td>
      <td>{readableDate(value.finish_date)}</td>
      {useEditableTableDateCell({
          propName: "workshop_invoice_date",
          value: value.workshop_invoice_date,
          onChange: onChange,
          onSave: safeSave,
          editable,
          setEditable,
          inlineEdit: true,
          disabled: value.po_approved
      })}
      <td>{value.description}</td>
      {useEditableTableTextAreaCell({
          propName: "invoice_comments",
          value: value.invoice_comments,
          onChange: onChange,
          onSave: onWorkSave,
          editable: workEditable,
          setEditable: setWorkEditable,
          inlineEdit: true,
          disabled: value.po_approved
      })}
      <td>£{value.labour_cost.toFixed(2)}</td>
      <td onClick={e => e.stopPropagation()}>
          <TaxTypePicker
            onChange={onChange}
            disabled={value.po_approved || !workEditable}
            name={"labour_cost_vat"}
            value={value.labour_cost_vat}
            className={"small-column"}
          />
      </td>
      <td onClick={e => e.stopPropagation()}>
          <WorkTypePicker
            className={"small-column"}
            work={value}
            options={workTypes}
            onSelect={onWorkTypeChange}
            disabled={!workEditable}
            showTitle={false}
            editable={workEditable}
            setEditable={setWorkEditable}
            onSave={onWorkSave}
            inlineEdit={false}
          />
      </td>
      <td onClick={e => e.stopPropagation()}>
        <NominalCodePicker
            name={"supplier_nominal_code"}
            inlineEdit={false}
            disabled={value.po_approved || !workEditable}
            value={value.supplier_nominal_code}
            editable={workEditable}
            blank={true}
            setEditable={setWorkEditable}
            onSave={onWorkSave}
            onChange={onNominalChange}
            options={nominalCodes}
        />
      </td>
      <td onClick={e => e.stopPropagation()}>
          <TrackingOptionsPicker
            value={value.supplier_tracking_option}
            options={trackingOptions}
            onSave={onWorkSave}
            onChange={onTrackingOptionChange}
            inlineEdit={false}
            disabled={value.po_approved || !workEditable}
            name={"supplier_tracking_option"}
            editable={workEditable}
            setEditable={setWorkEditable}
            blank={true}
          />
      </td>
      {value.work_type && value.work_type.approval_needed ?
          <td onClick={e => e.stopPropagation()}>
              <Control>
                  <Checkbox
                    checked={value.approval_received}
                    name={"approval_received"}
                    onChange={onApprovalReceived}
                    disabled={!workEditable}
                  />
              </Control>
          </td>
          :
          <td></td>
      }
      {value.work_type && value.work_type.approval_needed ?
        <td onClick={e => e.stopPropagation()}>
             <Select
                name={"warranty_claimed"}
                value={value.warranty_claimed}
                onChange={onChange}
                disabled={!workEditable}
             >
                <option key={0} value={"BLANK"}></option>
                <option key={1} value={"true"}>Yes</option>
                <option key={2} value={"false"}>No</option>
             </Select>
        </td> :
        <td></td>
      }

      <td onClick={e => e.stopPropagation()}>
          {
              <PaymentTypePicker
                options={paymentTypes}
                onChange={onPaymentTypeChange}
                value={value.payment_type}
                editable={workEditable}
                setEditable={setWorkEditable}
                blank={true}
                disabled={!workEditable}
                inlineEdit={false}
                className={"small-column"}
              />
          }
      </td>
      {useEditableTableTextCell({
        propName: (value.labour_cost < 0) ? "job_workshop_credit_number" : "job_workshop_invoice_number",
        value: (value.labour_cost < 0) ? value.job_workshop_credit_number : value.job_workshop_invoice_number,
        onChange: (value.labour_cost < 0) ? onCreditChange : onInvoiceChange,
        onSave: safeSave,
        editable,
        setEditable,
        inlineEdit: true,
        disabled: value.po_approved
      })}
      <td></td>
      <td></td>
      <td>Parts Total:</td>
      <td>
        £{value.parts.reduce((total, part) => total + part.cost, 0).toFixed(2)}
      </td>
      {approvedView &&
      <td>
        {value.po_approved && value.po_approver ? `${value.po_approver.first_name} ${value.po_approver.last_name}` : ""}
      </td>}
      <td onClick={e => e.stopPropagation()}>
        <Field>
          <Control className="table-checkbox">
            <Checkbox
              checked={selected}
              type="checkbox"
              onClick={onCheck}
              readOnly
            />
          </Control>
        </Field>
      </td>
      {!value.po_approved && (value.work_type && !value.work_type.free_of_charge && ( (value.work_type.approval_needed && value.approval_received) || (value.work_type.warranty_claim && value.warranty_claimed === "true") || (!value.work_type.approval_needed && !value.work_type.warranty_claim))) && (
        <td className="float-right">
          <Button
            color={value.po_ready_to_approve ? "success" : "info"}
            onClick={e => {
              e.stopPropagation();
              onApprove(value);
            }}
          >
            A
          </Button>
        </td>
      )}
    </tr>
  );

  rows.push(firstRow);

  const partRows = value.parts.map((part, index) => {
    return (
        <tr key={part.id} className="small-row-black">
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td>
                <Field className={"has-addons"}>
                    <Control>
                        <VatSelector
                            name={"cost_vat"}
                            value={part.cost_vat}
                            disabled={!partEditable}
                            onChange={e => onPartChange(e, value, index)}
                        />
                    </Control>
                    {partEditable ? (
                        <Control>
                            <Button className={"small-row-white"} color="success" onClick={() => onSavePart(index)}>
                                S
                            </Button>
                        </Control>
                    ) : (
                        <Control>
                            <Button
                                onClick={e => {
                                    e.stopPropagation()
                                    setPartEditable(!editable)}
                                }
                                className={"small-row-black"}
                            >
                                E
                            </Button>
                        </Control>
                    )}
                </Field>
            </td>
            <td></td>
            <td>
                <NominalCodePicker
                    value={part.supplier_nominal_code}
                    options={nominalCodes}
                    blank={true}
                    name={"supplier_nominal_code"}
                    disabled={value.po_approved || !partEditable}
                    editable={partEditable}
                    setEditable={setPartEditable}
                    inlineEdit={!value.po_approved}
                    onSave={() => onSavePart(index)}
                    onChange={(e) => onPartNominalChange(e, value, index)}
                />
            </td>
            <td>
                <TrackingOptionsPicker
                    options={trackingOptions}
                    blank={true}
                    name={"supplier_tracking_option"}
                    disabled={value.po_approved || !partEditable}
                    editable={partEditable}
                    setEditable={setPartEditable}
                    inlineEdit={!value.po_approved}
                    onSave={() => onSavePart(index)}
                    onChange={(e) => onPartTrackingOptionChange(e, value, index)}
                    value={part.supplier_tracking_option}
                />
            </td>
            <td></td>
            <td></td>
            <td></td>
            <td>{part.name}</td>
            <td>{part.part_number}</td>
            <td>{part.supplier.display_name}</td>
            <td>{formatCurrency(part.cost)}</td>
            <td></td>
            <td></td>
        </tr>
    );
  });

  rows = rows.concat(partRows);
  return rows;
};

export const InvoiceRows = ({
                              value,
                              printCheck,
                              selected,
                              endpoint,
                              refresh,
                              setInvoiceNo,
                              updateValue,
                              approvedView,
                              onStateChange,
                              nominalCodes,
                              codeObjects,
                              onCredit,
                              trackingOptions,
                              trackingOptionObjects
                            }) => {
  const [editable, setEditable] = useState(false);
  const [paidAmountEditable, setPaidAmountEditable] = useState(false);
  const [invoiceDateEditable, setInvoiceDateEditable] = useState(false);
  const [commentsEditable, setCommentsEditable] = useState(false);
  const [partsEditable, setPartsEditable] = useState(false);
  const [lastEdited, setLastEdited] = useState();

  const onCheck = e => {
    e.stopPropagation();
    printCheck(value);
  };

  const onChangeInvoice = e => {
    makeDirty();
    setLastEdited(new Date());
    setInvoiceNo(e.target.value);
  };

  const onChange = e => {
    makeDirty();
    setLastEdited(new Date());
    let newWork = {...value};
    newWork[e.target.name] = e.target.value;
    updateValue(newWork);
  };

  const onCodeChange = e => {
      const toUpdate = {
          target: {
              name: e.target.name,
              value: codeObjects[e.target.value]
          }
      }
      onChange(toUpdate);
  }

  const onTrackingOptionChange = e => {
      const toUpdate = {
          target: {
              name: e.target.name,
              value: trackingOptionObjects[e.target.value]
          }
      }
      onChange(toUpdate);
  }

  const onInvoiceDateSave = (work) => {
      // there needs to be some sort of switch for if its a quote and  not a job
      let url;
      if(work.job) {
          // get job
          url = endpoint + "jobs/" + work.job;
      }
      else{
          url =  endpoint + "quotes/" + work.quote
      }

      const conf = {
        method: "get",
        url
      };

      return axios(conf).then(resp => {
          let jobData = resp.data;
          if(work.chargeable){
              jobData.customer2_invoice_date= work.customer_invoice_date
          }
          else if(!work.chargeable){
              jobData.customer1_invoice_date= work.customer_invoice_date
          }
          const putConf = {
              url: url,
              method: "put",
              data: jobData
          }
          axios(putConf).then(res => {
              setInvoiceDateEditable(false);
          })
      })
  }

  const onAmountPaidSave = (work, customerAmountPaid) => {
      // get job
      const url = endpoint + "jobs/" + work.job;
      const conf = {
        method: "get",
        url
      };

      return axios(conf).then(resp => {
          let jobData = resp.data;
          if(work.chargeable){
              jobData.customer2_paid_amount= customerAmountPaid
          }
          else if(!work.chargeable){
              jobData.customer1_paid_amount= customerAmountPaid
          }
          const putConf = {
              url: url,
              method: "put",
              data: jobData
          }
          axios(putConf).then(res => {
              setPaidAmountEditable(false)
          })
      })
  }


  const onSave = e => {
    if (value.chargeable) {
        if(value.job) {
            return updateHireCustomerInvoice(
                endpoint,
                value.job,
                value.job_customer2_invoice_number
            ).then(() => {
                refresh();
                setEditable(!editable);
            });
        }
        else{
            return updateHireCustomerInvoice(
                endpoint,
                value.quote,
                value.job_customer2_invoice_number,
                null,
                null,
                null,
                null,
                null,
                true
            ).then(() => {
                refresh();
                setEditable(!editable);
            });
        }
    } else {
        if(value.job){
            return updateCustomerInvoice(
                endpoint,
                value.job,
                 value.job_customer_invoice_number
            ).then(() => {
                refresh();
                setEditable(!editable);
            });
        }
        else{
            return updateCustomerInvoice(
                endpoint,
                value.quote,
                value.job_customer_invoice_number,
                null,
                null,
                null,
                null,
                null,
                true
            ).then(() => {
                refresh();
                setEditable(!editable);
            });
        }
    }
  };

  const onSaveWork = e => {
    const conf = {
      method: "put",
      url: endpoint + 'work/update/' + value.id,
      data: value
    };
    return axios(conf).then(() => {
      refresh();
      setCommentsEditable(!commentsEditable)
    })
  };

  const onSavePart = (partIndex) => {
      const conf = {
          method: "put",
          url: endpoint+"parts/update/"+value.parts[partIndex].id,
          data: value.parts[partIndex]
      }
      axios(conf).then(res => {
          refresh();
          setPartsEditable(!partsEditable);
      }).catch(err => {
          toast.error("There was an error", {position: "top-right"});
      })
  };

  const checkUpdated = () => {
    return axios
      .get(endpoint + "work/" + value.id)
      .then(
        work =>
          (new Date(work.data.last_saved).getTime() -
            new Date(value.last_saved).getTime()) /
          1000 <
          1
      );
  };

  const [makeDirtyWork, safeSaveWork] = useSafeSave(
    onSaveWork,
    lastEdited,
    undefined,
    checkUpdated
  );

  const [makeDirty, safeSave] = useSafeSave(
    onSave,
    lastEdited,
    undefined,
    checkUpdated
  );

  const onPartNominalChange = (e, workItem, partIndex) =>{
      const toUpdate = {
          target: {
              name: e.target.name,
              value: codeObjects[e.target.value]
          }
      };
      onPartChange(toUpdate, workItem, partIndex)
  }

  const onPartCustomerPrePaidChange = (e, workItem, partIndex) => {
      const toUpdate = {
          target: {
              name: e.target.name,
              value: !workItem.parts[partIndex][e.target.name]
          }
      }
      onPartChange(toUpdate, workItem, partIndex)
  }

  const onPartTrackingOptionChange = (e, workItem, partIndex) =>{
      const toUpdate = {
          target: {
              name: e.target.name,
              value: trackingOptionObjects[e.target.value]
          }
      };
      onPartChange(toUpdate, workItem, partIndex)
  }

  const onPartChange = (e, workItem, partIndex) => {
      let newParts = [...workItem.parts];
      newParts[partIndex][e.target.name] = e.target.value;
      const toUpdate = {
          target: {
              name: "parts",
              value: newParts
          }
      };
      onChange(toUpdate);
  }

  let rows = [];
  const firstRow = (
    <InvoiceFirstRow
      endpoint={endpoint}
      selected={selected}
      safeSave={safeSave}
      safeSaveWork={safeSaveWork}
      onChange={onChange}
      onChangeInvoice={onChangeInvoice}
      value={value}
      editable={editable}
      commentsEditable={commentsEditable}
      onCheck={onCheck}
      setEditable={setEditable}
      setCommentsEditable={setCommentsEditable}
      approvedView={approvedView}
      onStateChange={onStateChange}
      nominalCodes={nominalCodes}
      onCodeChange={onCodeChange}
      onCredit={onCredit}
      trackingOptions={trackingOptions}
      onTrackingOptionChange={onTrackingOptionChange}
      paidEditable={paidAmountEditable}
      setPaidEditable={setPaidAmountEditable}
      onPaidAmountSave={onAmountPaidSave}
      invoiceDateEditable={invoiceDateEditable}
      setInvoiceDateEditable={setInvoiceDateEditable}
      onInvoiceDateSave={onInvoiceDateSave}
    />
  );

  rows.push(firstRow);

  const partRows = value.parts.map((part, index) => {
    return (
      <tr key={part.name + part.id} className="small-row-black">
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td>
            <NominalCodePicker
                value={part.customer_nominal_code}
                options={nominalCodes}
                blank={true}
                name={"customer_nominal_code"}
                disabled={value.invoice_approved || !partsEditable}
                editable={partsEditable}
                setEditable={setPartsEditable}
                inlineEdit={!value.invoice_approved}
                onSave={() => onSavePart(index)}
                onChange={(e) => onPartNominalChange(e, value, index)}
            />
        </td>
        <td>
            <TrackingOptionsPicker
                value={part.customer_tracking_option}
                options={trackingOptions}
                blank={true}
                name={"customer_tracking_option"}
                disabled={value.invoice_approved || !partsEditable}
                editable={partsEditable}
                setEditable={setPartsEditable}
                inlineEdit={!value.invoice_approved}
                onSave={() => onSavePart(index)}
                onChange={(e) => onPartTrackingOptionChange(e, value, index)}
            />
        </td>
        <td></td>
        <td></td>
        <td></td>
        <td>{part.name}</td>
        <td>{part.part_number}</td>
        <td>{part.part_invoice_number}</td>
        <td>{part.supplier.display_name}</td>
        <td>{formatCurrency(part.customer_cost)}</td>
        <td></td>
        <td></td>
        <td></td>
        {value.invoice_approved &&
            <td>
                <Button
                    color={"warning"}
                    onClick={() => onCredit(value, index)}
                >
                    Credit
                </Button>
            </td>
        }
      </tr>
    );
  });

  rows = rows.concat(partRows);
  return rows;
};

const InvoiceFirstRow = ({
                           value,
                           onChangeInvoice,
                           onChange,
                           safeSave,
                           safeSaveWork,
                           editable,
                           setEditable,
                           commentsEditable,
                           setCommentsEditable,
                           onCheck,
                           selected,
                           endpoint,
                           approvedView,
                           onStateChange,
                           nominalCodes,
                           onCodeChange,
                           onCredit,
                           onTrackingOptionChange,
                           trackingOptions,
                           paidEditable,
                           setPaidEditable,
                           onPaidAmountSave,
                           invoiceDateEditable,
                           setInvoiceDateEditable,
                           onInvoiceDateSave
                         }) => {
  // TODO: Shouldn't have to make a get request for each part here. The data should be in the value obj
  const [tableHighlight, setTableHighlight] = useState("");

  if (!value.quote && !tableHighlight) {
    setTableHighlight("table-highlight-orange");
  } else if (!tableHighlight) {
    let config = {
      url: endpoint + "worktotals/" + value.id
    };
    axios(config).then(result => {
      const costs = {...result.data};
      if (
        costs.total_estimate_labour_cost === costs.total_job_labour_cost &&
        costs.total_estimate_part_cost === costs.total_job_part_cost
      ) {
        setTableHighlight("table-highlight-orange");
      } else {
        setTableHighlight("table-highlight-red");
      }
    });
  }

  return (
      <tr
        className={`clickable ${tableHighlight} small-row-white`}
        onClick={() => window.open(`/editjob/${value.job}`, "_self")}
        key={value.id}
      >
          <td>{value.job ? value.job : value.quote}</td>
          <td>{value.vehicle_registration}</td>
          <td>{value.workshop}</td>
          <td>{value.chargeable ? value.customer2 : value.customer} </td>
          <td>{readableDate(value.finish_date)}</td>
          <td>{value.description}</td>
          <td>
              {useEditableTableDateCell({
                  propName: "customer_invoice_date",
                  value: value.customer_invoice_date,
                  editable: invoiceDateEditable,
                  setEditable: setInvoiceDateEditable,
                  inlineEdit: true,
                  onChange: onChange,
                  onSave: () => onInvoiceDateSave(value),
                  disabled: value.invoice_approved
              })}
          </td>
          <td>£{(value.customer_labour_cost || 0).toFixed(2)}</td>
          <td onClick={e => e.stopPropagation()}>
              <NominalCodePicker
                value={value.customer_nominal_code}
                options={nominalCodes}
                blank={true}
                name={"customer_nominal_code"}
                disabled={value.invoice_approved || !commentsEditable}
                editable={commentsEditable}
                setEditable={setCommentsEditable}
                inlineEdit={!value.invoice_approved}
                onSave={safeSaveWork}
                onChange={onCodeChange}
              />
          </td>
          <td onClick={e => e.stopPropagation()}>
            <TrackingOptionsPicker
                options={trackingOptions}
                onSave={safeSaveWork}
                onChange={onTrackingOptionChange}
                blank={true}
                name={"customer_tracking_option"}
                disabled={value.invoice_approved || !commentsEditable}
                editable={commentsEditable}
                setEditable={setCommentsEditable}
                inlineEdit={!value.invoice_approved}
                value={value.customer_tracking_option}
            />
          </td>
          {useEditableTableCostCell({
              propName: "customer_paid_amount",
              value: value.customer_paid_amount,
              onChange,
              editable: paidEditable,
              setEditable: setPaidEditable,
              inlineEdit: value.quote ? !value.quote.duplicate : true,
              disabled: value.quote ? !value.quote.duplicate : false,
              onSave: (e) => onPaidAmountSave(value, value.customer_paid_amount)
          })}
          <td>{value.job_workshop_invoice_number}</td>
          {useEditableTableTextCell({
            propName: value.chargeable
              ? "job_customer2_invoice_number"
              : "job_customer_invoice_number",
            value: value.chargeable
              ? value.job_customer2_invoice_number
              : value.job_customer_invoice_number,
            onChange: onChangeInvoice,
            onSave: safeSave,
            editable,
            setEditable,
            inlineEdit: true,
            disabled: value.invoice_approved
          })}
          <td></td>
          <td></td>
          <td></td>
          <td>Parts Total:</td>
          <td>
            £
            {value.parts
              .reduce((total, part) => total + part.customer_cost, 0)
              .toFixed(2)}
          </td>
          <td>{value.quote ? `QUOTE - ${value.quote}` : value.job ? "JOB" : "ERROR"}</td>
          {useEditableTableTextAreaCell({
            propName: 'invoice_comments',
            value: value.invoice_comments,
            onChange,
            onSave: safeSaveWork,
            editable: commentsEditable,
            setEditable: setCommentsEditable,
            inlineEdit: true,
            disabled: value.invoice_approved
          })}
          {approvedView &&
          <td>
            {value.invoice_approved && value.invoice_approver ? `${value.invoice_approver.first_name} ${value.invoice_approver.last_name}` : ""}
          </td>}
          <td onClick={e => e.stopPropagation()}>
            <Field>
              <Control className="table-checkbox">
                <Checkbox
                  checked={selected}
                  type="checkbox"
                  name="selectedForPrint"
                  onClick={onCheck}
                  readOnly
                />
              </Control>
            </Field>
          </td>
          {!value.invoice_approved && value.ready_to_approve && (
            <td className="float-right">
              <Button
                color="success"
                // className="is-pulled-right"
                onClick={e => {
                  e.stopPropagation();
                  onStateChange(value);
                }}
              >
                  A
              </Button>
            </td>
          )}
          {!value.invoice_approved && !value.ready_to_approve && (
            <td className="float-right">
              <Button
                color="info"
                // className="is-pulled-right"
                onClick={e => {
                  e.stopPropagation();
                  onStateChange(value);
                }}
              >
                R
              </Button>
            </td>
          )}
          {value.invoice_approved &&
            <td className={"float-right"} onClick={e=> e.stopPropagation()}>
                <Button
                    color={"warning"}
                    onClick={() => onCredit(value, null)}
                >
                    Credit
                </Button>
            </td>
          }
      </tr>
  );
};
